import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { Email, ExitToApp, VpnKey } from '@mui/icons-material';
import LoginForm from './components/LoginForm';
import ConfigurationPanel from './components/ConfigurationPanel';
import PasswordChangeDialog from './components/PasswordChangeDialog';
import { AuthService } from './services/AuthService';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

interface User {
  id: number;
  username: string;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await AuthService.getStatus();
      if (response.data.authenticated) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (username: string, password: string) => {
    try {
      setError('');
      const response = await AuthService.login(username, password);
      setUser(response.data.user);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Login failed');
    }
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handlePasswordChangeSuccess = () => {
    setError('');
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="sm">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
          >
            <CircularProgress />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <Email sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Email Assistant Configuration
            </Typography>
            <Chip 
              label="v1.1.5" 
              size="small" 
              color="secondary" 
              sx={{ mr: 2 }}
            />
            {user && (
              <>
                <Button
                  color="inherit"
                  onClick={() => setPasswordDialogOpen(true)}
                  startIcon={<VpnKey />}
                  sx={{ mr: 1 }}
                >
                  Change Password
                </Button>
                <Button
                  color="inherit"
                  onClick={handleLogout}
                  startIcon={<ExitToApp />}
                >
                  Logout ({user.username})
                </Button>
              </>
            )}
          </Toolbar>
        </AppBar>

        <Container maxWidth="md" sx={{ mt: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!user ? (
            <LoginForm onLogin={handleLogin} />
          ) : (
            <ConfigurationPanel />
          )}
          
          <PasswordChangeDialog
            open={passwordDialogOpen}
            onClose={() => setPasswordDialogOpen(false)}
            onSuccess={handlePasswordChangeSuccess}
          />
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
